import { TemplateFormDialogContentsSectionForm } from "./types";
import { CalendarEventReminder } from "@tellescope/types-models";
import { Button, Checkbox, Label, Textarea } from "@tellescope/ui";
import { Clock, Plus } from "lucide-react";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ReminderInput } from "../../dialogs/EventDialog/EventFormDialogContent/RemindersSection/ReminderInput";

export interface RemindersSectionProps {
  form: TemplateFormDialogContentsSectionForm<"instructions" | "resendRemindersOnAttendeeAdd">;
  reminders: CalendarEventReminder[];
  setReminders: Dispatch<SetStateAction<CalendarEventReminder[]>>;
}

export const RemindersSection = ({ form, reminders, setReminders }: RemindersSectionProps) => {
  const { t } = useTranslation("calendar");

  return (
    <section className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="instructions">{t("Instructions")}</Label>
          <Textarea
            id="instructions"
            {...form.getFieldProps("instructions")}
            placeholder={t("Instructions for attendees (usable in templates as {{calendar_event.instructions}})", {
              skipOnVariables: true,
            })}
          />
        </div>
        <span className="flex items-center gap-2">
          <Checkbox
            checked={form.values.resendRemindersOnAttendeeAdd}
            onCheckedChange={(checked) => form.setFieldValue("resendRemindersOnAttendeeAdd", !!checked)}
            id="resendRemindersOnAttendeeAdd"
          />
          <Label htmlFor="resendRemindersOnAttendeeAdd">
            {t("Resend reminders when a new contact is added as an attendee")}
          </Label>
        </span>
      </div>
      <div className="space-y-2">
        <span className="flex items-center justify-between">
          <Label>{t("Reminders")}</Label>
          <Button
            variant="outline"
            size="sm"
            className="h-auto py-1.5 font-light text-xs"
            onClick={() =>
              setReminders((prev) => [
                ...prev,
                {
                  type: "enduser-notification",
                  msBeforeStartTime: 1000 * 60 * 60,
                  info: {},
                },
              ])
            }
          >
            <Plus />
            {t("Add Reminder")}
          </Button>
        </span>
        {reminders.length ? (
          reminders.map((reminder, i) => (
            <ReminderInput
              key={`reminder-input-${i}`}
              value={reminder}
              onChange={(newVal) => setReminders((prev) => [...prev.slice(0, i), newVal, ...prev.slice(i + 1)])}
              onDelete={() => setReminders((prev) => [...prev.slice(0, i), ...prev.slice(i + 1)])}
            />
          ))
        ) : (
          <div className="w-full py-6 flex flex-col items-center border-muted border-2 rounded-md text-muted-foreground gap-2">
            <Clock size={32} className="text-gray-400" />
            <div className="flex flex-col gap-1 items-center">
              <p className="font-light text-sm">{t("No reminders set")}</p>
              <p className="font-light text-xs">{t('Click "Add Reminder" to create automatic notifications')}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
